// Port Tracking Content - Used inside Tracking Module
// This is the content portion without Navbar wrapper

import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../context/AuthContext";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Input } from "../../components/ui/input";
import { Button } from "../../components/ui/button";
import { Label } from "../../components/ui/label";
import { toast } from "sonner";
import { ReminderButton } from "../../components/ReminderComponents";
import { API_URL } from "../../config/api";
import {
  Anchor,
  Ship,
  Plus,
  Upload,
  FileSpreadsheet,
  Trash2,
  RefreshCw,
  Clock,
  Calendar,
  Mail,
  AlertCircle,
  CheckCircle,
  MapPin,
  Loader2,
  ChevronDown,
  ChevronUp,
  ExternalLink,
} from "lucide-react";


export default function PortTrackingContent() {
  const { getAuthHeaders, user } = useAuth();
  const [ports, setPorts] = useState([]);
  const [trackingEntries, setTrackingEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedPort, setSelectedPort] = useState("LUKA_KOPER"); // Default to Luka Koper
  const [containerNumber, setContainerNumber] = useState("");
  const [notificationEmails, setNotificationEmails] = useState("");
  const [europacificRef, setEuropacificRef] = useState("");
  const [pfNumber, setPfNumber] = useState("");
  const [showBulkImport, setShowBulkImport] = useState(false);
  const [bulkContainers, setBulkContainers] = useState("");
  const [dragActive, setDragActive] = useState(false);
  const [expandedEntry, setExpandedEntry] = useState(null);
  const [addingContainer, setAddingContainer] = useState(false);
  const [importingFile, setImportingFile] = useState(false);
  const [bulkAddingFromPFs, setBulkAddingFromPFs] = useState(false);

  // Port display info with icons and colors
  const portDisplayInfo = {
    "LUKA_KOPER": { flag: "🇸🇮", color: "blue", shortName: "Koper" },
    "RIJEKA_APM": { flag: "🇭🇷", color: "red", shortName: "APM Rijeka" },
    "RIJEKA_AGCT": { flag: "🇭🇷", color: "orange", shortName: "AGCT Rijeka" }
  };

  useEffect(() => {
    const fetchPorts = async () => {
      try {
        const response = await axios.get(`${API_URL}/ports`, { headers: getAuthHeaders() });
        setPorts(response.data);
        // Keep LUKA_KOPER as default, only change if it doesn't exist
        const koperExists = response.data.some(p => p.code === "LUKA_KOPER");
        if (!koperExists && response.data.length > 0) {
          setSelectedPort(response.data[0].code);
        }
      } catch (error) {
        console.error("Failed to fetch ports:", error);
      }
    };
    fetchPorts();
  }, [getAuthHeaders]);

  const fetchTrackingEntries = useCallback(async () => {
    setRefreshing(true);
    try {
      const response = await axios.get(`${API_URL}/port-tracking`, { headers: getAuthHeaders() });
      setTrackingEntries(response.data);
    } catch (error) {
      console.error("Failed to fetch tracking entries:", error);
      toast.error("Failed to fetch tracking entries");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [getAuthHeaders]);

  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      await fetchTrackingEntries();
      toast.success("Port tracking list refreshed");
    } catch (error) {
      toast.error("Failed to refresh");
    } finally {
      setRefreshing(false);
    }
  };

  const handleBulkAddFromPFs = async () => {
    if (!window.confirm("This will add all containers from 2026 Position Folders to port tracking at Luka Koper. Continue?")) {
      return;
    }
    
    setBulkAddingFromPFs(true);
    try {
      const response = await axios.post(
        `${API_URL}/port-tracking/bulk-from-pfs`,
        { port: selectedPort, year: 2026 },
        { headers: getAuthHeaders() }
      );
      
      const { added, skipped, errors } = response.data;
      toast.success(`Added ${added} containers to port tracking. Skipped: ${skipped}, Errors: ${errors}`);
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add containers from PFs");
    } finally {
      setBulkAddingFromPFs(false);
    }
  };

  useEffect(() => {
    fetchTrackingEntries();
  }, [fetchTrackingEntries]);

  const handleAddContainer = async (e) => {
    e.preventDefault();
    if (!containerNumber.trim() || !selectedPort) {
      toast.error("Please enter a container number and select a port");
      return;
    }

    setAddingContainer(true);
    try {
      const emails = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      
      await axios.post(
        `${API_URL}/port-tracking/add`,
        {
          container_number: containerNumber.trim(),
          port: selectedPort,
          notification_emails: emails,
          europacific_ref: europacificRef || null,
          pf_number: pfNumber || null
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Container ${containerNumber} added to ${selectedPort} tracking`);
      setContainerNumber("");
      setEuropacificRef("");
      setPfNumber("");
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add container");
    } finally {
      setAddingContainer(false);
    }
  };

  const handleBulkAdd = async () => {
    const containers = bulkContainers.split(/[\n,;]+/).map(c => c.trim()).filter(c => c);
    
    if (containers.length === 0) {
      toast.error("Please enter at least one container number");
      return;
    }

    setAddingContainer(true);
    try {
      const emails = notificationEmails.split(/[,;\s]+/).filter(e => e.trim());
      
      const response = await axios.post(
        `${API_URL}/port-tracking/bulk`,
        {
          container_numbers: containers,
          port: selectedPort,
          notification_emails: emails,
          pf_number: pfNumber || null
        },
        { headers: getAuthHeaders() }
      );

      toast.success(`Added ${response.data.added.length} containers, skipped ${response.data.skipped.length}`);
      setBulkContainers("");
      setShowBulkImport(false);
      setPfNumber("");
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add containers");
    } finally {
      setAddingContainer(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    const files = e.dataTransfer?.files || e.target.files;
    if (!files || files.length === 0) return;

    const file = files[0];
    
    if (file.type === "text/plain" || file.name.endsWith(".txt") || file.name.endsWith(".csv")) {
      const text = await file.text();
      setBulkContainers(text);
      toast.success("Text file loaded - review and click 'Add All'");
      return;
    }

    if (file.name.endsWith(".xlsx") || file.name.endsWith(".xls")) {
      await handleExcelImport(file);
    } else {
      toast.error("Supported formats: TXT, CSV, XLSX, XLS");
    }
  };

  const handleExcelImport = async (file) => {
    if (!selectedPort) {
      toast.error("Please select a port first");
      return;
    }

    setImportingFile(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("port", selectedPort);
      formData.append("notification_emails", notificationEmails);

      const response = await axios.post(
        `${API_URL}/port-tracking/import-xls`,
        formData,
        { headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" } }
      );

      toast.success(`Imported ${response.data.added.length} containers from file`);
      fetchTrackingEntries();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to import file");
    } finally {
      setImportingFile(false);
    }
  };

  const handleCheckNow = async (trackingId) => {
    try {
      toast.loading("Checking container status...");
      const response = await axios.post(
        `${API_URL}/port-tracking/${trackingId}/check-now`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.dismiss();
      
      if (response.data.check_result?.found) {
        toast.success(`Status: ${response.data.current_status || 'Found'}`);
      } else {
        toast.info("Container not found at port yet");
      }
      fetchTrackingEntries();
    } catch (error) {
      toast.dismiss();
      toast.error("Failed to check container");
    }
  };

  const handleDelete = async (trackingId, containerNumber) => {
    if (!window.confirm(`Stop tracking ${containerNumber}?`)) return;

    try {
      await axios.delete(`${API_URL}/port-tracking/${trackingId}`, { headers: getAuthHeaders() });
      toast.success("Tracking stopped");
      fetchTrackingEntries();
    } catch (error) {
      toast.error("Failed to stop tracking");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "active": return "text-green-500";
      case "completed": return "text-blue-500";
      case "expired": return "text-gray-500";
      default: return "text-yellow-500";
    }
  };

  return (
    <div className="max-w-[1800px] mx-auto p-6">
      {/* Add Container Form */}
      <div className="card-surface p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Plus className="h-5 w-5 text-primary" />
            Add Container to Track
          </h2>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleBulkAddFromPFs}
            disabled={bulkAddingFromPFs}
            className="text-blue-600 border-blue-300 hover:bg-blue-50"
            data-testid="bulk-add-from-pfs-btn"
          >
            {bulkAddingFromPFs ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Plus className="h-4 w-4 mr-2" />
            )}
            Add All 2026 PF Containers
          </Button>
        </div>

        {/* Port Selection - Improved Visual Cards */}
        <div className="mb-6">
          <Label className="text-sm font-medium text-foreground mb-3 block">Select Port</Label>
          <div className="grid grid-cols-3 gap-3">
            {ports.map((port) => {
              const info = portDisplayInfo[port.code] || { flag: "🏁", color: "gray", shortName: port.name };
              const isSelected = selectedPort === port.code;
              
              // Get explicit color classes based on port
              let selectedClasses = "border-blue-500 bg-blue-50 ring-2 ring-blue-200";
              let checkColor = "text-blue-500";
              if (port.code === "RIJEKA_APM") {
                selectedClasses = "border-red-500 bg-red-50 ring-2 ring-red-200";
                checkColor = "text-red-500";
              } else if (port.code === "RIJEKA_AGCT") {
                selectedClasses = "border-orange-500 bg-orange-50 ring-2 ring-orange-200";
                checkColor = "text-orange-500";
              }
              
              return (
                <button
                  key={port.code}
                  type="button"
                  onClick={() => setSelectedPort(port.code)}
                  className={`relative p-4 rounded-xl border-2 transition-all text-left ${
                    isSelected 
                      ? selectedClasses 
                      : "border-slate-200 bg-white hover:border-slate-300 hover:bg-slate-50"
                  }`}
                  data-testid={`port-select-${port.code}`}
                >
                  {isSelected && (
                    <div className="absolute top-2 right-2">
                      <CheckCircle className={`h-5 w-5 ${checkColor}`} />
                    </div>
                  )}
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{info.flag}</span>
                    <div>
                      <p className={`font-semibold ${isSelected ? "text-slate-800" : "text-slate-700"}`}>
                        {info.shortName}
                      </p>
                      <p className="text-xs text-slate-500">{port.country}</p>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        <form onSubmit={handleAddContainer} className="space-y-6">
          <div className="grid md:grid-cols-3 gap-4">
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Container Number</Label>
              <Input
                type="text"
                placeholder="e.g., TLLU4322906"
                value={containerNumber}
                onChange={(e) => setContainerNumber(e.target.value.toUpperCase())}
                className="font-mono"
                data-testid="port-tracking-container-input"
              />
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">Europacific Reference</Label>
              <Input
                type="text"
                placeholder="Optional reference"
                value={europacificRef}
                onChange={(e) => setEuropacificRef(e.target.value)}
              />
            </div>

            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">PF Number</Label>
              <Input
                type="text"
                placeholder="PF-000123-26"
                value={pfNumber}
                onChange={(e) => setPfNumber(e.target.value)}
              />
              <p className="text-xs text-muted-foreground mt-1">Link to Position Folder for customer notifications</p>
            </div>
          </div>

          <div>
            <Label className="text-sm text-muted-foreground mb-2 block flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Notification Emails (comma separated)
            </Label>
            <Input
              type="text"
              placeholder={`${user?.email || 'your@email.com'}, additional@email.com`}
              value={notificationEmails}
              onChange={(e) => setNotificationEmails(e.target.value)}
            />
            <p className="text-xs text-muted-foreground mt-1">Customer email from PF will be added automatically</p>
          </div>

          <div className="flex flex-wrap gap-3">
            <Button type="submit" disabled={addingContainer || !containerNumber.trim()} className="btn-primary" data-testid="port-tracking-add-btn">
              {addingContainer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Add Container
            </Button>
            <Button type="button" variant="outline" onClick={() => setShowBulkImport(!showBulkImport)}>
              <Upload className="h-4 w-4 mr-2" />
              Bulk Import
              {showBulkImport ? <ChevronUp className="h-4 w-4 ml-2" /> : <ChevronDown className="h-4 w-4 ml-2" />}
            </Button>
          </div>
        </form>

        {/* Bulk Import Section */}
        <AnimatePresence>
          {showBulkImport && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              exit={{ opacity: 0, height: 0 }}
              className="mt-6 pt-6 border-t border-border"
            >
              <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                <FileSpreadsheet className="h-5 w-5 text-primary" />
                Bulk Import Containers
              </h3>

              <div
                className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? "border-primary bg-primary/5" : "border-border"}`}
                onDragOver={(e) => { e.preventDefault(); setDragActive(true); }}
                onDragLeave={() => setDragActive(false)}
                onDrop={handleDrop}
              >
                <Upload className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-foreground font-medium mb-2">Drag & drop a file here, or click to browse</p>
                <p className="text-sm text-muted-foreground mb-4">Supported: XLSX, XLS, CSV, TXT</p>
                <input type="file" accept=".xlsx,.xls,.csv,.txt" onChange={handleDrop} className="hidden" id="file-upload" />
                <label htmlFor="file-upload" className="inline-flex items-center px-4 py-2 bg-primary text-white rounded-md cursor-pointer hover:bg-primary/90">
                  <FileSpreadsheet className="h-4 w-4 mr-2" />
                  {importingFile ? "Importing..." : "Browse Files"}
                </label>
              </div>

              <div className="mt-6">
                <Label className="text-sm text-muted-foreground mb-2 block">Or paste container numbers (one per line)</Label>
                <textarea
                  className="w-full h-32 rounded-md border border-input bg-background px-3 py-2 text-sm font-mono"
                  placeholder="TLLU4322906&#10;MSCU1234567&#10;CMAU9876543"
                  value={bulkContainers}
                  onChange={(e) => setBulkContainers(e.target.value.toUpperCase())}
                  data-testid="port-tracking-bulk-textarea"
                />
                <div className="flex justify-between items-center mt-3">
                  <span className="text-sm text-muted-foreground">{bulkContainers.split(/[\n,;]+/).filter(c => c.trim()).length} containers</span>
                  <Button onClick={handleBulkAdd} disabled={addingContainer || !bulkContainers.trim()} data-testid="port-tracking-bulk-add-btn">
                    {addingContainer ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add All to {selectedPort?.replace('_', ' ')}
                  </Button>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Tracking Entries List */}
      <div className="card-surface p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-foreground flex items-center gap-2">
            <Ship className="h-5 w-5 text-primary" />
            Tracked Containers ({trackingEntries.length})
          </h2>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="port-tracking-refresh-btn"
          >
            {refreshing ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4 mr-2" />
            )}
            Refresh
          </Button>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-muted-foreground mt-2">Loading...</p>
          </div>
        ) : trackingEntries.length === 0 ? (
          <div className="text-center py-12 bg-slate-50/50 rounded-lg border border-dashed border-slate-200">
            <Anchor className="h-12 w-12 text-muted-foreground mx-auto mb-4 opacity-50" />
            <p className="text-muted-foreground font-medium">No containers being tracked at ports</p>
            <p className="text-sm text-muted-foreground mt-1">
              Use the form above to add containers for port tracking
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {trackingEntries.map((entry) => (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="border border-border rounded-lg p-4 hover:border-primary/30 transition-colors"
              >
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <Ship className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-mono text-lg font-bold text-foreground">{entry.container_number}</p>
                        {entry.pf_number && (
                          <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {entry.pf_number}
                          </span>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-2 text-sm text-muted-foreground">
                        <span className="flex items-center gap-1"><MapPin className="h-3 w-3" />{entry.port_name}</span>
                        <span className={`flex items-center gap-1 ${getStatusColor(entry.status)}`}>
                          {entry.status === "active" ? <CheckCircle className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                          {entry.status}
                        </span>
                        {entry.customer_name && (
                          <span className="flex items-center gap-1 text-green-600">
                            <Mail className="h-3 w-3" />
                            {entry.customer_name}
                          </span>
                        )}
                      </div>
                      {entry.current_status && (
                        <p className="text-sm mt-1 text-primary font-medium">Port Status: {entry.current_status}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    {entry.direct_link && (
                      <Button variant="outline" size="sm" onClick={() => window.open(entry.direct_link, '_blank')} className="text-blue-600">
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                    <ReminderButton referenceType="port_tracking" referenceNumber={entry.container_number} getAuthHeaders={getAuthHeaders} />
                    <Button variant="outline" size="sm" onClick={() => handleCheckNow(entry.id)}><RefreshCw className="h-4 w-4" /></Button>
                    <Button variant="outline" size="sm" onClick={() => setExpandedEntry(expandedEntry === entry.id ? null : entry.id)}>
                      {expandedEntry === entry.id ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </Button>
                    <Button variant="outline" size="sm" onClick={() => handleDelete(entry.id, entry.container_number)} className="text-red-500">
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <AnimatePresence>
                  {expandedEntry === entry.id && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                      className="mt-4 pt-4 border-t border-border"
                    >
                      <div className="grid md:grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-muted-foreground flex items-center gap-2"><Calendar className="h-4 w-4" />Tracking Period</p>
                          <p className="text-foreground">{new Date(entry.start_date).toLocaleDateString()} - {new Date(entry.end_date).toLocaleDateString()}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-2"><Clock className="h-4 w-4" />Last Checked</p>
                          <p className="text-foreground">{entry.last_checked ? new Date(entry.last_checked).toLocaleString() : "Not yet"}</p>
                        </div>
                        <div>
                          <p className="text-muted-foreground flex items-center gap-2"><Mail className="h-4 w-4" />Notification Emails</p>
                          <p className="text-foreground">{entry.notification_emails?.join(", ") || "None"}</p>
                        </div>
                        {entry.europacific_ref && (
                          <div>
                            <p className="text-muted-foreground">Reference</p>
                            <p className="text-foreground font-mono">{entry.europacific_ref}</p>
                          </div>
                        )}
                        {entry.pf_number && (
                          <div>
                            <p className="text-muted-foreground">Position Folder</p>
                            <p className="text-foreground font-mono text-blue-600">{entry.pf_number}</p>
                          </div>
                        )}
                        {entry.customer_name && (
                          <div>
                            <p className="text-muted-foreground">Customer</p>
                            <p className="text-foreground">{entry.customer_name} {entry.customer_email && `(${entry.customer_email})`}</p>
                          </div>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
